import { RentalManagerUserInfoV2WebApiHandler_get_queryOptions } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { defineAuthProvider } from '@zg-rentals/auth-react/legacy';
import { getClient } from '@zg-rentals/ts-client-http-client';
import { createAuthPredicates } from '@zg-rentals/auth-comet/mfa';
import type { ReactNode } from 'react';
import { ErrorPage } from '@zg-rentals/error-page-components';
import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import type { RedirectHref } from '@zg-rentals/auth-react/legacy';
import { rentalManagerLoginOptions, rentalManagerStepUpLoginOptions } from '@zg-rentals/login-zillow';
import { zillowLoginRedirectHref } from '@zg-rentals/login-zillow/client';

const APP_NAME = 'rent-payment-web';

const httpClient = getClient().extend({
  prefixUrl: __DEV__ ? '/rental-manager/payments/proxy' : process.env.NEXT_PUBLIC_RENTAL_MANAGER_PREFIX_URL,
});

export type User = {
  email?: string;
  impersonatorEmail?: string;
  impersonatorUserToken?: string;
  isInitialMultiFactorAuthenticationRequired?: boolean;
  loggedIn: boolean;
  userToken?: string;
};

export const { hasInitiatedAuth, hasAuth } = createAuthPredicates<User>((user) => ({
  ...user,
  isLoggedIn: user.loggedIn,
}));

export const Auth = defineAuthProvider({
  queryOptions: () => RentalManagerUserInfoV2WebApiHandler_get_queryOptions({}, { httpClient }),
  isAuthorized: hasInitiatedAuth,
});

export const { useAuth } = Auth;

const ctaText = 'Return to Zillow Rental Manager';
const ctaUrl = '/rental-manager';

const onError = (errorMessage: string, errorCode: number) => (
  <ErrorPage
    errorMessage={errorMessage}
    errorDetails={`Error code: ${errorCode}`}
    action={<a href={ctaUrl}>{ctaText}</a>}
  />
);

const loginRedirectHref = zillowLoginRedirectHref({
  baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL,
  entryPoint: APP_NAME,
  forwardPrefix: process.env.NEXT_PUBLIC_AUTH_FORWARD_PREFIX,
  ...rentalManagerLoginOptions,
});

const mfaStepUpRedirectHref = zillowLoginRedirectHref({
  baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL,
  entryPoint: APP_NAME,
  forwardPrefix: process.env.NEXT_PUBLIC_AUTH_FORWARD_PREFIX,
  ...rentalManagerLoginOptions,
  ...rentalManagerStepUpLoginOptions,
});

const mfaRedirectHref: RedirectHref = (href, params) => {
  getGlobalMonitor()?.count(
    { name: 'mfa step-up attempt total' },
    { name: 'mfa step-up attempt type MfaStepUpAttempt' },
  );

  return mfaStepUpRedirectHref(href, params);
};

const onMfaRedirectAuthorized = () =>
  getGlobalMonitor()?.count(
    { name: 'mfa step-up success total' },
    { name: 'mfa step-up success type MfaStepUpSuccess' },
  );

const defaultBoundaryErrorProps = {
  onError: onError('unknown error', 500),
  onUnauthorized: () => onError('unauthorized', 401),
};

type MfaStepUpBoundaryProps = {
  children: ReactNode;
  loading?: ReactNode;
};

export const MfaStepUpBoundary = ({ children, loading = <>Loading...</> }: MfaStepUpBoundaryProps) => {
  const redirecting = <>Redirecting...</>;

  return (
    <Auth.Boundary
      {...defaultBoundaryErrorProps}
      loading={loading}
      redirectHref={loginRedirectHref}
      redirecting={redirecting}
    >
      <Auth.Boundary
        {...defaultBoundaryErrorProps}
        loading={loading}
        isAuthorized={hasAuth}
        redirectHref={mfaRedirectHref}
        redirecting={redirecting}
        onRedirectAuthorized={onMfaRedirectAuthorized}
        searchParam={['sp', 'x']}
      >
        {children}
      </Auth.Boundary>
    </Auth.Boundary>
  );
};
